// SNIGJ - Judicial Infrastructure Geolocation Page - Enhanced
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, Building2, Search, Phone, Clock, Navigation, Globe, 
  Plus, Edit, Trash2, Save, ExternalLink, Filter, List, Map,
  Scale, Shield, Lock, Briefcase, FileText, Users, Gavel
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Extended structure types for widgets - mapped from backend structure_types
const STRUCTURE_CATEGORIES = [
  { code: 'COMMISSARIAT', label: { fr: 'Commissariats', en: 'Police Stations' }, icon: '🚔', color: 'blue', types: ['SOUS_COMMISSARIAT', 'COMMISSARIAT'] },
  { code: 'TRIBUNAL', label: { fr: 'Tribunaux', en: 'Courts' }, icon: '⚖️', color: 'purple', types: ['TRIBUNAL_PAIX', 'TRIBUNAL_GRANDE_INSTANCE', 'TRIBUNAL_COMMERCE', 'TRIBUNAL_TRAVAIL'] },
  { code: 'PARQUET', label: { fr: 'Parquets', en: 'Prosecutors' }, icon: '📋', color: 'indigo', types: ['PARQUET_GRANDE_INSTANCE', 'PARQUET_GENERAL'] },
  { code: 'COUR', label: { fr: 'Cours', en: 'Courts of Appeal' }, icon: '🏛️', color: 'violet', types: ['COUR_APPEL', 'COUR_CASSATION', 'CONSEIL_ETAT', 'COUR_CONSTITUTIONNELLE'] },
  { code: 'PRISON', label: { fr: 'Prisons', en: 'Prisons' }, icon: '🏢', color: 'orange', types: ['PRISON_CENTRALE', 'PRISON_MAKALA'] },
  { code: 'CACHOT', label: { fr: 'Cachots', en: 'Detention' }, icon: '🔒', color: 'red', types: ['CACHOT_COMMISSARIAT', 'CACHOT_TRIBUNAL_PAIX', 'CACHOT_PARQUET_GI', 'CACHOT_COUR_APPEL'] },
  { code: 'NOTAIRE', label: { fr: 'Notaires', en: 'Notaries' }, icon: '📜', color: 'amber', types: ['NOTAIRE', 'ETUDE_NOTARIALE'] },
  { code: 'AVOCAT', label: { fr: 'Cabinets Avocats', en: 'Law Firms' }, icon: '👨‍⚖️', color: 'emerald', types: ['CABINET_AVOCAT', 'BARREAU'] },
];

// DRC Provinces
const PROVINCES = [
  'KINSHASA', 'KONGO-CENTRAL', 'KWANGO', 'KWILU', 'MAI-NDOMBE',
  'KASAI', 'KASAI-CENTRAL', 'KASAI-ORIENTAL', 'LOMAMI', 'SANKURU',
  'MANIEMA', 'SUD-KIVU', 'NORD-KIVU', 'ITURI', 'HAUT-UELE', 'TSHOPO',
  'BAS-UELE', 'NORD-UBANGI', 'MONGALA', 'SUD-UBANGI', 'EQUATEUR',
  'TSHUAPA', 'TANGANYIKA', 'HAUT-LOMAMI', 'LUALABA', 'HAUT-KATANGA'
];

const JudicialMapPage = () => {
  const { user, language } = useAuth();
  const isAdmin = ['NATIONAL_ADMIN', 'SUPER_ADMIN', 'TECH_SUPPORT'].includes(user?.role);
  
  const [structures, setStructures] = useState([]);
  const [structureTypes, setStructureTypes] = useState({ provincial: [], national: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterProvince, setFilterProvince] = useState('all');
  const [filterJusticeType, setFilterJusticeType] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load from structures API (jurisdictional_structures collection)
      const [structuresRes, typesRes] = await Promise.all([
        api.get('/structures'),
        api.get('/structure-types')
      ]);
      
      setStructures(structuresRes.data || []);
      setStructureTypes(typesRes.data || { provincial: [], national: [] });
    } catch (error) {
      console.error('Failed to load structures:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get category for a structure type
  const getCategoryForType = (structureType) => {
    for (const cat of STRUCTURE_CATEGORIES) {
      if (cat.types.includes(structureType)) {
        return cat;
      }
    }
    return null;
  };

  // Calculate stats for each category
  const categoryStats = STRUCTURE_CATEGORIES.map(cat => {
    const count = structures.filter(s => cat.types.includes(s.structure_type)).length;
    return { ...cat, count };
  });

  // Get all structure types from backend
  const allStructureTypes = [...(structureTypes.provincial || []), ...(structureTypes.national || [])];

  // Filter structures
  const filteredStructures = structures.filter(struct => {
    const matchesSearch = !searchQuery || 
      struct.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      struct.province?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      struct.address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesProvince = filterProvince === 'all' || struct.province === filterProvince;
    const matchesJusticeType = filterJusticeType === 'all' || struct.justice_type === filterJusticeType;
    
    let matchesCategory = filterCategory === 'all';
    if (!matchesCategory) {
      const category = STRUCTURE_CATEGORIES.find(c => c.code === filterCategory);
      if (category) {
        matchesCategory = category.types.includes(struct.structure_type);
      }
    }
    
    return matchesSearch && matchesProvince && matchesCategory && matchesJusticeType;
  });

  // Get structure type info
  const getStructureTypeInfo = (typeCode) => {
    const type = allStructureTypes.find(t => t.code === typeCode);
    return type || { code: typeCode, name: typeCode };
  };

  // Get icon for structure
  const getStructureIcon = (structureType) => {
    const category = getCategoryForType(structureType);
    if (category) {
      switch (category.code) {
        case 'COMMISSARIAT': return <Shield className="w-5 h-5 text-blue-600" />;
        case 'TRIBUNAL': return <Scale className="w-5 h-5 text-purple-600" />;
        case 'PARQUET': return <FileText className="w-5 h-5 text-indigo-600" />;
        case 'COUR': return <Gavel className="w-5 h-5 text-violet-600" />;
        case 'PRISON': return <Building2 className="w-5 h-5 text-orange-600" />;
        case 'CACHOT': return <Lock className="w-5 h-5 text-red-600" />;
        case 'NOTAIRE': return <FileText className="w-5 h-5 text-amber-600" />;
        case 'AVOCAT': return <Briefcase className="w-5 h-5 text-emerald-600" />;
        default: return <Building2 className="w-5 h-5 text-gray-600" />;
      }
    }
    return <Building2 className="w-5 h-5 text-gray-600" />;
  };

  // Open in Google Maps
  const openInMaps = (struct) => {
    if (struct.latitude && struct.longitude) {
      window.open(`https://www.google.com/maps?q=${struct.latitude},${struct.longitude}`, '_blank');
    } else if (struct.address) {
      window.open(`https://www.google.com/maps/search/${encodeURIComponent(struct.address + ', ' + (struct.province || '') + ', RDC')}`, '_blank');
    } else {
      toast.info(language === 'fr' ? 'Coordonnées GPS non disponibles' : 'GPS coordinates not available');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="judicial-map-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
              <MapPin className="w-7 h-7 text-primary" />
              {language === 'fr' ? 'Carte Judiciaire' : 'Judicial Map'}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">
              {language === 'fr' 
                ? 'Toutes les structures juridictionnelles de la RDC' 
                : 'All jurisdictional structures of DRC'}
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => window.location.href = '/admin/structures'} className="gap-2">
              <Plus className="w-4 h-4" />
              {language === 'fr' ? 'Gérer' : 'Manage'}
            </Button>
          )}
        </div>

        {/* Stats Summary - All Categories */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {categoryStats.map(cat => (
            <Card 
              key={cat.code} 
              className={`cursor-pointer hover:border-primary transition-all ${filterCategory === cat.code ? 'border-primary ring-2 ring-primary/20' : ''}`}
              onClick={() => setFilterCategory(filterCategory === cat.code ? 'all' : cat.code)}
            >
              <CardContent className="p-3 text-center">
                <span className="text-2xl">{cat.icon}</span>
                <p className="text-xl sm:text-2xl font-bold mt-1">{cat.count}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{cat.label[language] || cat.label.fr}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Total count */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <Badge variant="outline" className="text-base px-3 py-1">
            <Building2 className="w-4 h-4 mr-2" />
            {structures.length} {language === 'fr' ? 'structures au total' : 'total structures'}
          </Badge>
          {filterCategory !== 'all' && (
            <Button variant="ghost" size="sm" onClick={() => setFilterCategory('all')}>
              {language === 'fr' ? 'Réinitialiser le filtre' : 'Reset filter'}
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-structures"
                />
              </div>
              
              <Select value={filterJusticeType} onValueChange={setFilterJusticeType}>
                <SelectTrigger className="w-[160px]">
                  <Scale className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Justice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toute justice' : 'All Justice'}</SelectItem>
                  <SelectItem value="CIVIL">⚖️ {language === 'fr' ? 'Civile' : 'Civil'}</SelectItem>
                  <SelectItem value="MILITAIRE">⚔️ {language === 'fr' ? 'Militaire' : 'Military'}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterProvince} onValueChange={setFilterProvince}>
                <SelectTrigger className="w-[180px]">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Toutes les provinces' : 'All Provinces'}</SelectItem>
                  {PROVINCES.map(province => (
                    <SelectItem key={province} value={province}>{province}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-1 border rounded-md p-1">
                <Button 
                  variant={viewMode === 'list' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === 'map' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('map')}
                >
                  <Map className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : viewMode === 'list' ? (
          filteredStructures.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">
                  {language === 'fr' ? 'Aucune structure trouvée' : 'No structures found'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {structures.length === 0 
                    ? (language === 'fr' 
                        ? 'Créez des structures dans Administration > Structures'
                        : 'Create structures in Administration > Structures')
                    : (language === 'fr' 
                        ? 'Essayez de modifier vos filtres'
                        : 'Try adjusting your filters')}
                </p>
                {isAdmin && structures.length === 0 && (
                  <Button onClick={() => window.location.href = '/admin/structures'}>
                    {language === 'fr' ? 'Créer une structure' : 'Create Structure'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStructures.map(struct => {
                const typeInfo = getStructureTypeInfo(struct.structure_type);
                const category = getCategoryForType(struct.structure_type);
                
                return (
                  <Card 
                    key={struct.structure_id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => { setSelectedStructure(struct); setShowDetailModal(true); }}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {getStructureIcon(struct.structure_type)}
                          <div>
                            <CardTitle className="text-base">{struct.name}</CardTitle>
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {typeInfo.name || struct.structure_type}
                              </Badge>
                              {struct.justice_type === 'MILITAIRE' && (
                                <Badge variant="outline" className="text-xs text-red-600 border-red-300 bg-red-50">
                                  ⚔️
                                </Badge>
                              )}
                              {struct.has_cachot && (
                                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Cachot
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <Badge variant={struct.level === 'NATIONAL' ? 'default' : 'secondary'} className="text-[10px]">
                          {struct.level}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {struct.province && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{struct.province}</span>
                        </div>
                      )}
                      
                      {struct.address && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 flex-shrink-0 opacity-50" />
                          <span className="truncate">{struct.address}</span>
                        </div>
                      )}
                      
                      {struct.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <a href={`tel:${struct.phone}`} className="text-primary hover:underline" onClick={e => e.stopPropagation()}>
                            {struct.phone}
                          </a>
                        </div>
                      )}

                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-3 gap-2"
                        onClick={(e) => { e.stopPropagation(); openInMaps(struct); }}
                      >
                        <Navigation className="w-4 h-4" />
                        {language === 'fr' ? 'Itinéraire' : 'Directions'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        ) : (
          // Map View Placeholder
          <Card>
            <CardContent className="p-0 overflow-hidden rounded-lg">
              <div className="bg-muted h-[500px] flex items-center justify-center">
                <div className="text-center p-8">
                  <Map className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium text-lg mb-2">
                    {language === 'fr' ? 'Vue Carte' : 'Map View'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {language === 'fr' 
                      ? 'Cliquez sur une structure dans la liste pour voir son emplacement sur Google Maps'
                      : 'Click on a structure in the list to view its location on Google Maps'}
                  </p>
                  <Button onClick={() => setViewMode('list')}>
                    {language === 'fr' ? 'Voir la liste' : 'View List'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedStructure && getStructureIcon(selectedStructure.structure_type)}
                {selectedStructure?.name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedStructure && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Badge>{getStructureTypeInfo(selectedStructure.structure_type).name}</Badge>
                  <Badge variant={selectedStructure.level === 'NATIONAL' ? 'default' : 'secondary'}>
                    {selectedStructure.level}
                  </Badge>
                  {selectedStructure.justice_type === 'MILITAIRE' && (
                    <Badge variant="destructive">⚔️ Militaire</Badge>
                  )}
                  {selectedStructure.has_cachot && (
                    <Badge variant="outline" className="text-orange-600">
                      <Lock className="w-3 h-3 mr-1" /> Cachot
                    </Badge>
                  )}
                </div>

                <div className="space-y-3 text-sm">
                  {selectedStructure.province && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      <span><strong>Province:</strong> {selectedStructure.province}</span>
                    </div>
                  )}
                  {selectedStructure.address && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span><strong>Adresse:</strong> {selectedStructure.address}</span>
                    </div>
                  )}
                  {selectedStructure.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${selectedStructure.phone}`} className="text-primary hover:underline">
                        {selectedStructure.phone}
                      </a>
                    </div>
                  )}
                  {selectedStructure.email && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <a href={`mailto:${selectedStructure.email}`} className="text-primary hover:underline">
                        {selectedStructure.email}
                      </a>
                    </div>
                  )}
                  {(selectedStructure.latitude && selectedStructure.longitude) && (
                    <div className="flex items-center gap-3">
                      <Navigation className="w-4 h-4 text-muted-foreground" />
                      <span>GPS: {selectedStructure.latitude}, {selectedStructure.longitude}</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setShowDetailModal(false)}>
                    {language === 'fr' ? 'Fermer' : 'Close'}
                  </Button>
                  <Button className="flex-1 gap-2" onClick={() => openInMaps(selectedStructure)}>
                    <Navigation className="w-4 h-4" />
                    {language === 'fr' ? 'Voir sur la carte' : 'View on Map'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default JudicialMapPage;
